<template>
  <nav class="navbar is-fixed-top is-primary">
    <div class="container">
      <div class="navbar-brand">
        <span
          class="connection-icon fa-lg"
          @click="displayConnectionError"
          :title="$t(connectionError)"
          v-if="!connection"
        >
          <i class="fa fa-warning"></i>
        </span>
        <span
          class="connection-icon fa-lg"
          @click="displayTimeoutError"
          :title="$t(timeoutError)"
          v-if="connection && hasPendingActions"
        >
          <i class="fa fa-spinner fa-spin"></i>
        </span>
        <span
          class="connection-icon fa-lg"
          :title="$t(connectionOK)"
          v-if="connection && !hasPendingActions"
        >
          <i class="fa fa-wifi"></i>
        </span>
        <span class="navbar-item">
          <span class="has-text-weight-bold">chefCarte</span>
          <span v-if="isProjectInitialized">&nbsp;:: {{ project.name }}</span>
        </span>

        <button
          v-if="isUserLogged"
          class="button navbar-burger is-primary"
          :class="{ 'is-active': isActive }"
          @click="toggleMenu"
        >
          <span></span> <span></span> <span></span>
        </button>
      </div>
      <div
        class="navbar-menu"
        v-if="isUserLogged"
        :class="{ 'is-active': isActive }"
      >
        <div class="navbar-start" v-if="isProjectInitialized">
          <div class="navbar-item has-dropdown is-hoverable">
            <a class="navbar-link">{{ $t("Offers") }}</a>
            <div class="navbar-dropdown">
              <router-link
                class="navbar-item"
                v-for="offer in offers"
                :key="offer.id"
                :class="{ 'is-active': isCurrentRoute(`/${offer.id}`) }"
                :to="`/projects/${project.id}/offers/${offer.id}`"
                @click.native="closeMenu"
              >
                {{ offer.name }}</router-link
              >
              <hr class="navbar-divider" v-if="isAdmin && offers.length > 0" />
              <router-link
                v-if="isAdmin"
                class="navbar-item"
                :class="{ 'is-active': isCurrentRoute('/offers-management') }"
                :to="`/projects/${project.id}/offers-management`"
                @click.native="closeMenu"
              >
                <span class="icon is-small"> <i class="fa fa-cog"></i> </span>
                <span>{{ $t("Offers management") }}</span>
              </router-link>
            </div>
          </div>

          <router-link
            class="navbar-item"
            :class="{ 'is-active': isCurrentRoute('/meals') }"
            :to="`/projects/${project.id}/meals`"
            @click.native="closeMenu"
            >{{ $t("Meals") }}
          </router-link>

          <router-link
            class="navbar-item"
            :class="{ 'is-active': isCurrentRoute('/screens') }"
            :to="`/projects/${project.id}/screens`"
            @click.native="closeMenu"
            >{{ $t("Screens") }}
          </router-link>
        </div>
        <div class="navbar-end">
          <div
            v-if="isUserLogged"
            class="navbar-item has-dropdown is-hoverable"
          >
            <a class="navbar-link">{{ $t("Locations") }}</a>
            <div class="navbar-dropdown">
              <router-link
                class="navbar-item"
                v-for="(projectName, projectId) in projects"
                :key="projectId"
                :to="`/projects/${projectId}`"
                @click.native="closeMenu"
              >
                <span class="icon is-small" v-if="projectId === project.id">
                  <i class="fa fa-check"></i>
                </span>
                <span>{{ projectName }}</span>
                <span v-if="projectId === project.id">
                  &nbsp;({{ $t(userRole) }})
                </span>
              </router-link>
              <hr class="navbar-divider" v-if="projects" />
              <router-link
                class="navbar-item"
                :class="{ 'is-active': isCurrentRoute('/settings') }"
                :to="`/projects/${project.id}/settings`"
                @click.native="closeMenu"
                v-if="isAdmin && isProjectInitialized"
              >
                <span class="icon is-small"> <i class="fa fa-cog"></i> </span>
                <span>{{ $t("Location settings") }}</span>
              </router-link>
              <router-link class="navbar-item" to="/" @click.native="closeMenu">
                <span class="icon is-small"> <i class="fa fa-home"></i> </span>
                <span>{{ $t("Locations list") }}</span>
              </router-link>
            </div>
          </div>

          <div class="navbar-item has-dropdown is-hoverable">
            <a class="navbar-link">{{ userName }}</a>
            <div class="navbar-dropdown">
              <router-link
                class="navbar-item"
                :class="{ 'is-active': isCurrentRoute('/account-settings') }"
                :to="`/account-settings`"
                @click.native="closeMenu"
              >
                <span class="icon is-small"> <i class="fa fa-cog"></i> </span>
                <span>{{ $t("Account settings") }}</span>
              </router-link>
              <hr class="navbar-divider" />
              <a href="#" class="navbar-item" @click.prevent="onLogout">
                <span class="icon is-small">
                  <i class="fa fa-sign-out"></i>
                </span>
                <span>{{ $t("Log Out") }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { state, actions } from "@/store"

// TODO extract navbar options for different UCs (project-manager, project, auth)
// TODO create navbar it within these components
export default {
  name: "navbar",
  data() {
    return {
      isActive: false,
      connectionError: "Cannot connect to server",
      timeoutError: "Sending action. Please be patient.",
      connectionOK: "Connected"
    }
  },
  computed: {
    user() {
      return state.user
    },
    isUserLogged() {
      return state.isUserLogged
    },
    isProjectInitialized() {
      return state.isProjectInitialized
    },
    userRole() {
      return state.userRole
    },
    isAdmin() {
      return state.isAdmin
    },
    userName() {
      return this.user.displayName || this.user.email
    },
    projects() {
      return state.user.projects
    },
    project() {
      return state.project
    },
    offers() {
      return Object.keys(state.project.data.offers).map(offerId => ({
        id: offerId,
        name: state.project.data.offers[offerId].conf.name || "Offer"
      }))
    },
    connection() {
      return state.connection
    },
    hasPendingActions() {
      return state.pendingActionsSize > 0
    }
  },
  methods: {
    toggleMenu() {
      this.isActive = !this.isActive
    },
    closeMenu() {
      this.isActive = false
    },
    onLogout() {
      this.closeMenu()
      actions.logout()
      this.$router.push("/login")
    },
    isCurrentRoute(path) {
      const routePath = this.$route.path
      return routePath.substring(routePath.lastIndexOf("/")) === path
    },
    displayConnectionError() {
      actions.addDisappearingError(this.connectionError)
    },
    displayTimeoutError() {
      actions.addDisappearingError(this.timeoutError)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/style.scss";

.connection-icon {
  line-height: 3rem !important;
  font-size: 1.75rem;
  margin-left: 1rem;
  width: 1.5rem;
}
.navbar-item {
  .icon {
    margin-right: 0.5rem;
  }
}
.button:focus:not(:active),
.button.is-focused:not(:active) {
  box-shadow: none !important;
}
.navbar-burger {
  position: absolute;
  right: 0;
}
@include desktop {
  .navbar-dropdown {
    max-height: 90vh;
    overflow-y: auto;
  }
  /* fix hidding dropdown navbar */
  .navbar-item.is-hoverable:hover .navbar-dropdown {
    display: block !important;
  }
  .navbar-item.is-hoverable:focus-within .navbar-dropdown {
    display: none;
  }
}
</style>
